import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@core/http";
import { lookupCode } from "./api";
import { BigButton, Notice } from "../access/primitives";
import "./home.css";

// Adaptación de la hoja de `Inicio` (`validador.jsx:284-300`) — SPEC-302 §4.
const MESSAGES = {
  qrInvalid: "Código no válido. Verifica que sea un código de Star Circle",
  noConnection: "Sin conexión. Revisa la señal o el wifi del local",
};

function minutesFromRetryAfter(retryAfterSeconds: number | null | undefined): {
  minutes: number;
  until: Date;
} {
  // SPEC-302 §8: "N = ceil(problem.retryAfterSeconds / 60) (si falta, 1)".
  const seconds =
    retryAfterSeconds === null ||
    retryAfterSeconds === undefined ||
    retryAfterSeconds <= 0
      ? 60
      : retryAfterSeconds;
  const minutes = Math.ceil(seconds / 60);
  return { minutes, until: new Date(Date.now() + seconds * 1000) };
}

function minutesRemaining(until: Date): number {
  return Math.max(1, Math.ceil((until.getTime() - Date.now()) / 60000));
}

export function ManualCodeSheet({
  bearerToken,
  blockedUntil,
  offline,
  onBlocked,
  onClose,
}: {
  bearerToken: string;
  blockedUntil: Date | null;
  offline: boolean;
  onBlocked: (until: Date) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [invalidCode, setInvalidCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isBlocked =
    blockedUntil !== null && blockedUntil.getTime() > Date.now();
  // Sin red no hay nada que validar (encargo SPEC-302 §7): la hoja sigue
  // visible pero deshabilita el campo y el botón, sin mensaje propio — el
  // aviso "Sin conexión" ya está en `HomeScreen`, detrás de esta hoja.
  const disabled = isBlocked || submitting || offline;

  async function handleSearch() {
    if (disabled || !code.trim()) return;
    setSubmitting(true);
    setError(null);
    setInvalidCode(null);
    try {
      const data = await lookupCode(code, "manual", bearerToken);
      onClose();
      navigate("/validator/confirm", {
        state: { code: data, entry: "manual" },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "QR_INVALID") {
          setError(MESSAGES.qrInvalid);
          setInvalidCode(code);
        } else if (err.status === 429 || err.code === "MANUAL_BLOCKED") {
          const { until } = minutesFromRetryAfter(
            err.problem?.retryAfterSeconds,
          );
          onBlocked(until);
        } else {
          // 409/422 (`QR_EXPIRED`, `QR_USED`, `MERCHANT_NOT_ENABLED`, etc.):
          // rechazo ya resuelto por el lookup — se navega directo a
          // `ResultScreen` (R-03/SPEC-304), sin pasar por `Confirmar`.
          onClose();
          navigate("/validator/result", { state: { problem: err.problem } });
        }
      } else {
        setError(MESSAGES.noConnection);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="sc-manual-overlay"
      onClick={() => {
        onClose();
      }}
    >
      <div
        className="sc-manual-sheet"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="sc-manual-handle" />
        <h2 className="sc-manual-title">Código manual</h2>
        <p className="sc-manual-copy">
          El miembro ve el código bajo el QR, en la app.
        </p>
        {isBlocked ? (
          <Notice>
            {`Demasiados intentos. El ingreso manual se habilita en ${String(
              minutesRemaining(blockedUntil),
            )} minutos. Puedes seguir escaneando`}
          </Notice>
        ) : null}
        {error ? <Notice>{error}</Notice> : null}
        <input
          value={code}
          onChange={(event) => {
            setCode(event.target.value.toUpperCase());
            setInvalidCode(null);
          }}
          placeholder="SC-0000-0000"
          disabled={disabled}
          className="sc-manual-input"
        />
        {invalidCode ? (
          <div className="sc-manual-code-echo">{invalidCode}</div>
        ) : null}
        <BigButton
          onClick={() => {
            void handleSearch();
          }}
          disabled={disabled}
        >
          {submitting ? "Buscando…" : "Buscar código"}
        </BigButton>
        <div className="sc-manual-gap" />
        <BigButton
          tone="ghost"
          onClick={() => {
            onClose();
          }}
        >
          Cancelar
        </BigButton>
      </div>
    </div>
  );
}
